/* eslint-disable arrow-body-style */
import { formatters } from "@garantidos/utils"

import removeSpecialChars from "@mobi/utils/formatters/removeSpecialChars"

import { ltv } from "constants"

const {
  removeCharsExceptNumbers,
  currencyToFloat,
  toPercent,
  toCurrency,
  toDate,
  toTextYears
} = formatters

const validatorsDictionary = {
  minAge: {
    rule: ({ conditionValue }) => {
      return (value) => !isUnderAge(value, conditionValue)
    },
    message: ({ conditionValue = 18 }) =>
      `A idade mínima é de ${conditionValue} anos`
  },

  distinctDocument: {
    rule: ({ proposalData: { cpf } }) => {
      return (value) => {
        return removeSpecialChars(value) !== cpf
      }
    },
    message: () => "CPF do companheiro(a) não pode ser igual ao do proponente"
  },

  maxFinancingTerm: {
    rule: ({ conditionValue, proposalData: { property_type } }) => {
      return (value) => {
        const condition = conditionValue[property_type]
        if (!condition || !property_type) return true
        return maxYears({ value, conditionValue: condition })
      }
    },
    message: ({ conditionValue, proposalData: { property_type } }) => {
      const condition = conditionValue[property_type]
      if (!condition) return ""
      return `Prazo máximo de financiamento é de ${condition} anos`
    }
  },

  greaterThanZero: {
    rule: () => {
      return (value) => value > 0
    },
    message: () => "Valor da renda deve ser maior que R$ 0,00"
  },

  differentEmailFromOriginal: {
    rule: ({ proposalData: { email } }) => {
      return (value) => value !== email
    },
    message: () => "O e-mail do companheiro(a) deve ser diferente do proponente"
  },

  differentTelephone: {
    rule: ({ proposalData: { proponent_phone } }) => {
      return (value) => {
        if (!value) return true
        return (
          removeSpecialChars(value)?.replaceAll(" ", "") !== proponent_phone
        )
      }
    },
    message: () => "Os dois telefones de contatos não podem ser iguais"
  },

  differentTelephoneOriginal: {
    rule: ({ proposalData: { proponent_phone, phone_2 } }) => {
      return (value) => {
        if (!value) return true
        return (
          removeSpecialChars(value)?.replaceAll(" ", "") !== proponent_phone &&
          removeSpecialChars(value)?.replaceAll(" ", "") !== phone_2
        )
      }
    },
    message: () =>
      "O telefone do companheiro(a) não pode ser igual ao proponente"
  },

  hasSpecialChar: {
    rule: () => {
      return (value) => {
        const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/
        return !specialChars.test(value)
      }
    },
    message: () => "O endereço não pode conter caracteres especiais"
  },

  minFinancingValue: {
    rule: ({
      conditionValue,
      proposalData: { property_type, property_value }
    }) => {
      return (value) => {
        const condition = conditionValue[property_type]
        if (!condition) return true
        return minFinancing({
          conditionValue: condition,
          propertyValue: property_value,
          inputValue: currencyToFloat(value)
        })
      }
    },
    message: ({ conditionValue, proposalData: { property_type } }) => {
      const condition = conditionValue[property_type]
      if (!condition) return ""
      return `Valor a ser financiado deve ser maior ou igual a ${toCurrency(
        condition
      )}`
    }
  },

  minPropertyValue: {
    rule: ({ conditionValue }) => {
      return (value) => {
        return value >= conditionValue
      }
    },
    message: ({ conditionValue }) => {
      const condition = conditionValue
      if (!condition) return ""
      return "Valor inválido"
    }
  },

  maxPropertyValue: {
    rule: ({ conditionValue }) => {
      return (value) => {
        return value <= conditionValue
      }
    },
    message: ({ conditionValue }) => {
      const condition = conditionValue
      if (!condition) return ""
      return "Valor inválido"
    }
  },

  minCreditValue: {
    rule: ({ conditionValue }) => {
      return (value) => {
        return value >= conditionValue
      }
    },
    message: ({
      conditionValue,
      conditionMaxCreditValue = ltv,
      proposalData: { property_value }
    }) => {
      const condition = conditionValue
      const maxValue = toCurrency(property_value * conditionMaxCreditValue)
      if (!condition) return ""
      if (!property_value) return "Valor mínimo: R$ 30.000,00"
      return `Valor inválido`
    }
  },

  maxCreditValue: {
    rule: ({ conditionValue, proposalData: { property_value } }) => {
      return (value) => {
        return property_value * conditionValue >= value
      }
    },
    message: ({ conditionValue, proposalData: { property_value } }) => {
      const condition = conditionValue
      const maxValue = toCurrency(property_value * condition)
      if (!condition) return ""
      return `Valor inválido`
    }
  },

  checkMaxAge: {
    rule: ({ conditionValue = 80.5, proposalData: { financing_term } }) => {
      return (value) => {
        if (!financing_term) return true
        const financingTerm =
          Number.parseFloat(
            removeCharsExceptNumbers(toTextYears(financing_term))
          ) || 0
        const partnerAge = getAge(value) || conditionValue + 1
        const isLowerThanLimit = financingTerm + partnerAge <= conditionValue
        return isLowerThanLimit
      }
    },
    message: ({ conditionValue = 80.5 }) => {
      const years = Math.floor(conditionValue)
      const months = (conditionValue * 12) % 12
      const textYears = years ? `${years} ${years === 1 ? "ano" : "anos"}` : ""
      const textMonths = months
        ? `${months} ${months === 1 ? "mês" : "meses"}`
        : ""
      const textConnector = years && months ? " e " : ""
      const msg = `A soma da idade com o prazo de financiamento não pode ultrapassar ${textYears}${textConnector}${textMonths}`
      return msg
    }
  },

  inputValue: {
    rule: ({
      conditionValue,
      proposalData: { property_type, property_value }
    }) => {
      return (value) => {
        const condition = conditionValue[property_type]
        if (!condition) return true
        if (!property_value) return true
        const inputValue = currencyToFloat(value)
        const { minInputRatio, minFinancingValue } = condition
        const hasMinInput = minInput({
          propertyValue: property_value,
          inputValue,
          ratio: minInputRatio
        })
        const hasMaxInput = maxInput({
          propertyValue: property_value,
          inputValue,
          minFinancingValue
        })
        return hasMinInput && hasMaxInput
      }
    },
    message: ({
      conditionValue,
      rules: { minFinancingValue },
      proposalData: { property_type, property_value }
    }) => {
      const condition = conditionValue[property_type]
      if (!condition) return ""
      const minFinancing = minFinancingValue[property_type]
      const errorMessage = getFinancingRatioErrorMessage({
        propertyType: property_type,
        propertyValue: property_value,
        minFinancingValue: minFinancing,
        financingRatio: condition
      })
      return errorMessage
    }
  },

  finacingTermWithAge: {
    rule: ({ conditionValue = 80.5, proposalData: { birthdate } }) => {
      return (value) => {
        const financingTerm =
          Number.parseFloat(removeCharsExceptNumbers(value)) || 0
        const _birthdate = toDate(birthdate) || 0
        if (!_birthdate) return true
        const age = getAge(_birthdate) || conditionValue + 1
        const isLowerThanLimit = financingTerm + age <= conditionValue || !age
        return isLowerThanLimit
      }
    },

    message: ({ conditionValue = 80.5 }) => {
      const years = Math.floor(conditionValue)
      const months = (conditionValue * 12) % 12
      const textYears = years ? `${years} ${years === 1 ? "ano" : "anos"}` : ""
      const textMonths = months
        ? `${months} ${months === 1 ? "mês" : "meses"}`
        : ""
      const textConnector = years && months ? " e " : ""
      const msg = `A soma da idade com o prazo de financiamento não pode ultrapassar ${textYears}${textConnector}${textMonths}`
      return msg
    }
  },

  finacingTermWithAgePartner: {
    rule: ({ conditionValue = 80.5, proposalData: { financing_term } }) => {
      return (value) => {
        if (!financing_term) return true
        const financingTerm =
          Number.parseFloat(
            removeCharsExceptNumbers(toTextYears(financing_term))
          ) || 0
        const partnerAge = getAge(value) || conditionValue + 1
        const isLowerThanLimit = financingTerm + partnerAge <= conditionValue
        return isLowerThanLimit
      }
    },
    message: ({ conditionValue = 80.5 }) => {
      const years = Math.floor(conditionValue)
      const months = (conditionValue * 12) % 12
      const textYears = years ? `${years} ${years === 1 ? "ano" : "anos"}` : ""
      const textMonths = months
        ? `${months} ${months === 1 ? "mês" : "meses"}`
        : ""
      const textConnector = years && months ? " e " : ""
      const msg = `A soma da idade com o prazo de financiamento não pode ultrapassar ${textYears}${textConnector}${textMonths}`
      return msg
    }
  }
}
export default validatorsDictionary

//UTILS

const maxYears = ({ value, conditionValue }) => {
  return Number(removeCharsExceptNumbers(value)) <= conditionValue
}

const getAge = (dateString) => {
  if (!dateString) return 0
  const [day, month, year] = dateString.split("/")
  const date = `${month}/${day}/${year}`

  const today = new Date()
  const birthDate = new Date(date)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  let decimalMonths = parseFloat(m / 12)
  if (today.getMonth() - month > 0) {
    decimalMonths += 0.01
  }
  const d = today.getDay() - birthDate.getDate()
  let decimalDays = parseFloat(d / 12 / 30)
  if (today.getDate() - day > 0) {
    decimalDays += 0.01
  }

  return age + decimalMonths + decimalDays
}

export const minFinancing = ({ conditionValue, propertyValue, inputValue }) => {
  if (!propertyValue || !inputValue) return true
  const hasMinFinancingValue = propertyValue - inputValue >= conditionValue
  return hasMinFinancingValue
}

export const isUnderAge = (birthday, age = 18) => {
  const instances = birthday.split("/")
  const [day, month, year] = instances
  return (
    new Date(Number(year) + age, Number(month) - 1, day, "00", "00", "00") >
    new Date()
  )
}

const minInput = ({ propertyValue, inputValue, ratio }) => {
  const hasMinInput = inputValue >= propertyValue * ratio
  return hasMinInput
}

const maxInput = ({ propertyValue, inputValue, minFinancingValue }) => {
  const hasMaxInput = inputValue <= propertyValue - minFinancingValue
  return hasMaxInput
}

const getFinancingRatioErrorMessage = ({
  propertyType,
  propertyValue,
  minFinancingValue,
  financingRatio
}) => {
  const { minInputRatio } = financingRatio
  if (propertyType === "RESIDENCIAL") {
    const maxInputRatio = 1 - minInputRatio
    const msg = `Valor de entrada deve ser entre ${toPercent(
      minInputRatio
    )} e ${toPercent(maxInputRatio)} do valor do imóvel`
    return msg
  }
  const minInputValue = propertyValue * minInputRatio
  const maxInputValue = propertyValue - minFinancingValue
  const msg = `O valor de entrada deve ser entre ${toCurrency(
    minInputValue
  )} e ${toCurrency(maxInputValue)}`
  return msg
}
